import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { keys } from "@/lib/queries";
import { fetchMeOrNull } from "@/lib/auth";
import { useAuthModal } from "@/components/auth/AuthModalProvider";
import { joinLeagueByCode } from "@/lib/rank";
import { useToast } from "@/lib/toast";
import { Spinner, EmptyState, Button } from "@/components/ui";
import { buildHead, SITE } from "@/lib/seo";

// Deep-link join resolver: /rank/join/<code>. Auto-joins when signed in;
// otherwise opens the login modal and replays the join on success, then lands
// on the league detail.
export const Route = createFileRoute("/_app/rank_/join/$code")({
  // Invite links get a proper preview (they are shared person-to-person),
  // but the codes themselves must never be indexed.
  head: ({ params }) =>
    buildHead({
      path: `/rank/join/${params.code}`,
      title: "Join a league",
      description: `You've been invited to a private prediction league on ${SITE.name}.`,
      noindex: true,
    }),
  component: JoinByLink,
});

function JoinByLink() {
  const { t } = useTranslation("rank");
  const { code } = Route.useParams();
  const nav = useNavigate();
  const auth = useAuthModal();
  const toast = useToast();
  const me = useQuery({ queryKey: keys.me(), queryFn: fetchMeOrNull });
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(true);
  const done = useRef(false);

  const runJoin = useCallback(async () => {
    if (done.current) return;
    done.current = true;
    setJoining(true);
    setError(null);
    try {
      const league = await joinLeagueByCode(code);
      toast.push({ title: t("join.joined", { name: league.name }), kind: "success" });
      nav({ to: "/rank/$leagueId", params: { leagueId: league.league_id }, replace: true });
    } catch {
      setError(t("joinLink.invalid"));
      setJoining(false);
    }
  }, [code, nav, toast, t]);

  useEffect(() => {
    if (me.isLoading) return;
    if (me.data) {
      void runJoin();
    } else {
      setJoining(false);
      auth.open({
        onSuccess: () => {
          done.current = false;
          void runJoin();
        },
      });
    }
    // run once auth state resolves
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.isLoading, me.data]);

  if (error) {
    return (
      <div className="px-4 pt-10">
        <EmptyState
          title={t("joinLink.failedTitle")}
          description={error}
          action={
            <Button onClick={() => nav({ to: "/rank", search: { tab: "private" } })}>{t("joinLink.goToRank")}</Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-[50dvh] flex-col items-center justify-center gap-3 px-4 text-center">
      {joining ? (
        <>
          <Spinner size={28} />
          <p className="text-sm text-fg-muted">{t("joinLink.joining")}</p>
        </>
      ) : (
        <>
          <p className="text-sm text-fg-muted">{t("joinLink.signInPrompt")}</p>
          <Button
            onClick={() =>
              auth.open({
                onSuccess: () => {
                  done.current = false;
                  void runJoin();
                },
              })
            }
          >
            {t("signIn")}
          </Button>
        </>
      )}
    </div>
  );
}
